// ─────────────────────────────────────────────────────────────────────
// CONSOLE CAPTURE — a small ring buffer that records the most recent
// console.error/console.warn calls and uncaught errors/rejections, so the
// bug-report flow can attach the session's recent log output. Extracted
// verbatim from the former monolith (legacy/App.jsx). No secrets here.
//
// `setupConsoleCapture()` is idempotent and installed once at app start; it
// wraps console.error/warn (still calling the originals) and listens for
// window 'error' / 'unhandledrejection'. `getCapturedConsoleLog()` returns the
// buffered entries as formatted text. `__consoleLog__` is the ring buffer
// itself — BugReportModal reads `__consoleLog__.length` to show a count.
//
// Types are intentionally loose (any), matching the former monolith's untyped style.
// ─────────────────────────────────────────────────────────────────────

const CONSOLE_LOG_CAPACITY = 50;
export const __consoleLog__: any[] = []; // [{ level, message, time, stack? }]
let __consoleCaptureInstalled__ = false;

export function setupConsoleCapture() {
  if (__consoleCaptureInstalled__ || typeof window === 'undefined') return;
  __consoleCaptureInstalled__ = true;

  const push = (entry: any) => {
    __consoleLog__.push({ ...entry, time: Date.now() });
    if (__consoleLog__.length > CONSOLE_LOG_CAPACITY) {
      __consoleLog__.shift();
    }
  };

  // Wrap console.error / console.warn — call the original so we don't
  // steal logging from DevTools.
  const origError = console.error.bind(console);
  const origWarn = console.warn.bind(console);
  console.error = (...args: any[]) => {
    push({ level: 'error', message: args.map((a) => formatLogArg(a)).join(' ') });
    origError(...args);
  };
  console.warn = (...args: any[]) => {
    push({ level: 'warn', message: args.map((a) => formatLogArg(a)).join(' ') });
    origWarn(...args);
  };

  // Uncaught errors and promise rejections
  window.addEventListener('error', (e: any) => {
    push({
      level: 'uncaught',
      message: e.message || 'Uncaught error',
      stack: e.error?.stack,
    });
  });
  window.addEventListener('unhandledrejection', (e: any) => {
    push({
      level: 'rejection',
      message: String(e.reason?.message || e.reason || 'Unhandled rejection'),
      stack: e.reason?.stack,
    });
  });
}

function formatLogArg(a: any): string {
  if (a == null) return String(a);
  if (typeof a === 'string') return a;
  if (a instanceof Error) return `${a.name}: ${a.message}${a.stack ? '\n' + a.stack : ''}`;
  try {
    return JSON.stringify(a);
  } catch {
    return String(a);
  }
}

// Fetch the captured log — returns formatted text suitable for a bug report
export function getCapturedConsoleLog(): string {
  if (__consoleLog__.length === 0) return '';
  return __consoleLog__
    .map((entry) => {
      const t = new Date(entry.time).toLocaleTimeString('no-NO');
      const lvl = entry.level.toUpperCase().padEnd(8);
      return `[${t}] ${lvl} ${entry.message}${entry.stack ? '\n' + entry.stack : ''}`;
    })
    .join('\n');
}
